// assetFrontend_1_bak/src/components/PermissionGuard.jsx
import React from 'react';
import { usePermissions } from '../context/PermissionContext';

/**
 * PermissionGuard component - conditionally renders children based on permissions
 *
 * Usage:
 * <PermissionGuard resource="bookmark" action="create">
 *   <Button>Add Bookmark</Button>
 * </PermissionGuard>
 *
 * Or with multiple permission checks:
 * <PermissionGuard
 *   anyOf={[
 *     { resource: 'bookmark', action: 'create' },
 *     { resource: 'bookmark', action: 'edit' }
 *   ]}
 * >
 *   <Button>Manage Bookmarks</Button>
 * </PermissionGuard>
 */
const PermissionGuard = ({
  children,
  resource,
  action,
  anyOf,
  allOf,
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  // Don't render anything while permissions are loading
  if (loading) {
    return fallback;
  }

  // Check for anyOf permissions (user needs at least one)
  if (anyOf && Array.isArray(anyOf)) {
    if (!hasAnyPermission(anyOf)) {
      return fallback;
    }
    return <>{children}</>;
  }

  // Check for allOf permissions (user needs all of them)
  if (allOf && Array.isArray(allOf)) {
    if (!hasAllPermissions(allOf)) {
      return fallback;
    }
    return <>{children}</>;
  }

  // Check for single resource/action permission
  if (resource && action) {
    if (!hasPermission(resource, action)) {
      return fallback;
    }
    return <>{children}</>;
  }

  // If no permission check is specified, render children by default
  return <>{children}</>;
};

export default PermissionGuard;
